import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Search, UserPlus, CheckCircle, Mail } from 'lucide-react';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { UnitType } from '@/types';
import { unitTypeRequiresNumber } from '@/lib/unit-number-display';
import {
  findUnitForJoin,
  checkPendingMembershipRequest,
  requestUnitMembership,
  type JoinableUnit,
} from '@masonic-tools/shared-auth';

export default function JoinUnitPage() {
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [unitName, setUnitName] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [foundUnit, setFoundUnit] = useState<JoinableUnit | null>(null);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [searched, setSearched] = useState(false);
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const selectedUnitType = unitTypes.find((ut) => ut.id === selectedTypeId);
  const entityName = selectedUnitType?.entityName || 'Unit';
  const showUnitNumber = unitTypeRequiresNumber(selectedUnitType);

  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        let types: UnitType[] = [];

        if (settingsDoc.exists()) {
          types = settingsDoc.data().unitTypes || [];
        }

        setUnitTypes(types);
        if (types.length > 0) {
          setSelectedTypeId(types[0].id);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      showError('You must be logged in to join a unit');
      return;
    }

    if (!currentUser.emailVerified) {
      showError('Please verify your email address before requesting membership.');
      return;
    }

    if (!selectedTypeId || !unitName.trim() || (showUnitNumber && !unitNumber.trim())) {
      showError('Please fill in all fields');
      return;
    }

    setSearching(true);
    setFoundUnit(null);
    setHasPendingRequest(false);
    setAlreadyMember(false);
    setSearched(false);

    try {
      const unit = await findUnitForJoin(db, unitName, unitNumber, selectedTypeId);

      if (!unit) {
        setSearched(true);
        return;
      }

      const memberships = userProfile?.units || [];
      const isMember = memberships.some(
        (m: { unitId?: string; lodgeId?: string }) =>
          m.unitId === unit.id || m.lodgeId === unit.id
      );

      if (isMember) {
        setFoundUnit(unit);
        setAlreadyMember(true);
        setSearched(true);
        return;
      }

      const pending = await checkPendingMembershipRequest(db, unit.id, currentUser.uid);
      setFoundUnit(unit);
      setHasPendingRequest(pending.hasPending);
      setSearched(true);
    } catch (error) {
      console.error('Error searching for unit:', error);
      showError('Failed to search for unit. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleRequestMembership = async () => {
    if (!currentUser || !foundUnit) return;

    setSubmitting(true);
    try {
      await requestUnitMembership(
        db,
        foundUnit.id,
        foundUnit.name,
        currentUser.uid,
        currentUser.email!,
        unitName.trim(),
        unitNumber.trim(),
        'direct_request'
      );

      showSuccess('Membership request sent! The unit secretary will be notified.');
      navigate('/dashboard');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to send membership request.';
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser?.emailVerified) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Verify Your Email</CardTitle>
              <CardDescription>
                Please verify your email address before requesting to join a lodge or chapter.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <Mail className="h-4 w-4" />
                <AlertDescription>
                  Check your inbox for a verification link, or visit your account settings to resend it.
                </AlertDescription>
              </Alert>
              <Button variant="outline" className="w-full" onClick={() => navigate('/settings')}>
                Go to Settings
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (unitTypes.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Unit Types Not Configured</CardTitle>
              <CardDescription>
                Unit types must be configured before you can search for a lodge or chapter.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Join a Lodge or Chapter</CardTitle>
            <CardDescription>
              Search for your existing lodge or chapter and request membership. A unit officer will review your request.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="unitType">Type *</Label>
                <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                  <SelectTrigger id="unitType">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {unitTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({type.entityName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="unitName">Name *</Label>
                <Input
                  id="unitName"
                  type="text"
                  placeholder={`e.g., Veritas ${entityName}`}
                  value={unitName}
                  onChange={(e) => setUnitName(e.target.value)}
                  required
                />
              </div>

              {showUnitNumber && (
                <div className="space-y-2">
                  <Label htmlFor="unitNumber">Number *</Label>
                  <Input
                    id="unitNumber"
                    type="text"
                    placeholder="e.g., No. 1234"
                    value={unitNumber}
                    onChange={(e) => setUnitNumber(e.target.value)}
                    required
                  />
                </div>
              )}

              <Button type="submit" className="w-full" disabled={searching || !selectedTypeId}>
                <Search className="mr-2 h-4 w-4" />
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </form>

            {searched && !foundUnit && (
              <Alert>
                <Building2 className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-1">No matching unit found</p>
                  <p className="text-sm">
                    We could not find an approved {entityName.toLowerCase()} with that name and number.
                    Check your details, or{' '}
                    <Link to="/unit/create" className="underline font-medium">
                      create a new application
                    </Link>{' '}
                    if your lodge or chapter is not yet on the platform.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {foundUnit && alreadyMember && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-1">You are already a member</p>
                  <p className="text-sm">
                    You are already a member of {foundUnit.name}{showUnitNumber && foundUnit.unitNumber ? ` (${foundUnit.unitNumber})` : ''}.
                  </p>
                  <Button
                    className="mt-3"
                    size="sm"
                    onClick={() => navigate(`/unit/${foundUnit.id}/dashboard`)}
                  >
                    Go to Unit Dashboard
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {foundUnit && !alreadyMember && (
              <div className="rounded-lg border p-4 space-y-4">
                <div>
                  <p className="font-semibold">{foundUnit.name}</p>
                  {showUnitNumber && foundUnit.unitNumber && (
                    <p className="text-sm text-muted-foreground">{foundUnit.unitNumber}</p>
                  )}
                  {selectedUnitType && (
                    <p className="text-xs text-muted-foreground mt-1">{selectedUnitType.name}</p>
                  )}
                </div>

                {hasPendingRequest ? (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      You already have a pending membership request for this unit. The secretary will review it shortly.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Button
                    className="w-full"
                    onClick={handleRequestMembership}
                    disabled={submitting}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    {submitting ? 'Sending Request...' : 'Request Membership'}
                  </Button>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2 border-t">
              <p className="text-sm text-muted-foreground text-center">
                Need to register a new lodge or chapter?
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/unit/create')}>
                Create New Application
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
